import { useState, useEffect, useRef, useContext, memo } from "react";
import { Type, TypeDescriptionElement } from "./ontology";
import { SituationMentionAnnotationScheme } from "./scheme";
import { EntityMention } from "../model/entities";
import { UUID } from "../model/uuid";
import SearchBar from "../UI/SearchBar";
import matcher from "../utils/regexMatcher";
import OntologyType from "../UI/OntologyType";
import { SituationMention } from "../model/situations";
import { MentionModelContext, MentionAnnotationContext } from "../contexts";
import { ToggleButton } from "../UI/ToggleButton";
import {
	initializeRoleSetMap,
	initializeSituationExpressionTree,
} from "../utils/situationOntologyScripts";
import { Node, mentionTypeCheck } from "../utils/expressionTree";
import { EntityMentionAnnotationScheme } from "./scheme";
import { MentionModel } from "../communication";

type MentionAnnotationScheme =
	| EntityMentionAnnotationScheme
	| SituationMentionAnnotationScheme;

interface MentionAnnotationProps {
	activeStructureIds: [UUID, UUID, MentionModel][];
	activeSituation: UUID[];
	annotationScheme: MentionAnnotationScheme;
	mentionList: EntityMention[] | SituationMention[];
	activeTypeIds: UUID[];
	roleOntologyType?: string;
	spanMap: Map<string, string>;
	activateStructureIds: (
		uuids: UUID[],
		tokenizationIds: UUID[],
		mentionModel: MentionModel
	) => UUID[];
	deactivateStructureIds: () => UUID[];
	activateTypeIds: (uuid?: UUID) => void;
	setHighlightedMentions: (mentionList: Set<string>) => void;
	activateSituation?: (uuids: UUID[]) => void;
	deactivateSituation?: () => void;
}

function MentionAnnotation(props: MentionAnnotationProps) {
	const mentionModel = useContext(MentionModelContext);
	const primaryTypeRef = useRef<Type[]>(_initializePrimaryTypeRef());
	const roleSetMap = useRef<Map<string, Type[]>>(
		props.annotationScheme.subkind === "situation"
			? initializeRoleSetMap(
					props.annotationScheme.roleOntologyType,
					props.annotationScheme as SituationMentionAnnotationScheme
			  )
			: new Map<string, Type[]>()
	);
	const situationExpressionTreeMap = useRef<Map<string, Node>>(
		initializeSituationExpressionTree(
			props.roleOntologyType,
			props.annotationScheme as SituationMentionAnnotationScheme
		)
	);

	const typeToMentionListMap = useRef<Map<string, Set<string>>>(
		new Map<string, Set<string>>()
	);
	const disabledMap = new Map<string, boolean>();

	const [argumentTypeMap, setArgumentTypeMap] = useState<
		Map<string, Map<string, string>>
	>(new Map<string, Map<string, string>>());

	const {
		spanToTypeMap,
		setSpanToTypeMap,
		situationArgumentMap,
		setSituationArgumentMap,
		displayToast,
		addToUserInteractions,
	} = useContext(MentionAnnotationContext);

	const [focusedType, setFocusedType] = useState<number | undefined>(
		undefined
	); //TODO: Might be enough to use props.activeTyeId
	const [typeList, setTypeList] = useState<Type[]>([]); //TODO: Indices instead of TypeRefElement
	const [searchOpen, setSearchOpen] = useState<boolean>(false);
	const [searchText, setSearchText] = useState<string>("");
	const [firstRun, setFirstRun] = useState<boolean>(true);

	function _isArrayOfStrings(value: unknown): value is string[] {
		return (
			Array.isArray(value) &&
			value.every((item) => typeof item === "string")
		);
	}

	function _getDescription(
		description: string | TypeDescriptionElement[]
	): string {
		if (Array.isArray(description)) {
			return description
				.map((val) => {
					if (Array.isArray(val)) {
						return val.map((innerVal) => {
							return innerVal.value;
						});
					} else {
						return val.value;
					}
				})
				.join(" ");
		} else {
			return description;
		}
	}

	function _getExample(example: string[] | TypeDescriptionElement[][]) {
		if (_isArrayOfStrings(example)) {
			return example.join(" ");
		} else {
			return example
				.map((val) => {
					if (Array.isArray(val)) {
						return val
							.map((innerVal) => {
								return innerVal.value;
							})
							.join(" ");
					} else if ("value" in val) {
						return val;
					}
				})
				.join(", ");
		}
	}

	function _initializePrimaryTypeRef(): Type[] {
		const typeArray = [];
		const types = props.annotationScheme.ontology
			? props.annotationScheme.ontology.types
			: [];
		for (const type of types) {
			const newElement: Type = {
				value: type.value,
				description: type.description,
				examples: type.examples,
				uuid: type.uuid,
				kind: type.kind,
			};

			typeArray.push(newElement);
		}
		return typeArray;
	}

	function _initializeSpanToTypeMap() {
		const map = new Map<string, string>();

		typeToMentionListMap.current.forEach((mentions, type) => {
			mentions.forEach((mention) => {
				map.set(mention, type);
			});
		});

		return map;
	}

	useEffect(() => {
		if (focusedType !== undefined) {
			const _activeSituation = props.activeSituation.length
				? props.activeSituation[0].uuidString
				: "";
			props.activateTypeIds(typeList[focusedType].uuid);
			props.setHighlightedMentions(
				typeToMentionListMap.current.get(typeList[focusedType].value) ||
					situationArgumentMap
						.get(_activeSituation)
						?.get(typeList[focusedType].value) ||
					new Set<string>()
			);
		} else if (
			focusedType === undefined &&
			props.activeSituation.length &&
			props.activeStructureIds
		) {
			const highlightedMentions = new Set<string>();
			situationArgumentMap
				.get(props.activeSituation[0].uuidString)
				?.forEach((mentions, role) => {
					mentions.forEach((mention) => {
						highlightedMentions.add(mention);
					});
				});
			props.setHighlightedMentions(highlightedMentions);
		} else {
			props.activateTypeIds();
			props.setHighlightedMentions(new Set<string>());
		}
	}, [focusedType]);

	useEffect(() => {
		setFirstRun(false);
		primaryTypeRef.current = _initializePrimaryTypeRef();
		roleSetMap.current =
			props.annotationScheme.subkind === "situation"
				? initializeRoleSetMap(
						props.annotationScheme.roleOntologyType,
						props.annotationScheme as SituationMentionAnnotationScheme
				  )
				: new Map<string, Type[]>();
		situationExpressionTreeMap.current = initializeSituationExpressionTree(
			props.roleOntologyType,
			props.annotationScheme as SituationMentionAnnotationScheme
		);
		typeToMentionListMap.current = new Map<string, Set<string>>();
	}, [props.annotationScheme]);

	useEffect(() => {
		if (!firstRun) {
			props.mentionList.forEach((span) => {
				primaryTypeRef.current.forEach((type) => {
					if (!typeToMentionListMap.current.has(type.value)) {
						typeToMentionListMap.current.set(
							type.value,
							new Set<string>()
						);
					}
					if (
						"entityType" in span &&
						type.value === span.entityType &&
						props.spanMap.has(span.uuid.uuidString)
					) {
						typeToMentionListMap.current
							.get(type.value)!
							.add(span.uuid.uuidString);
					} else if (
						"situationType" in span &&
						type.value === span.situationType &&
						props.spanMap.has(span.uuid.uuidString)
					) {
						typeToMentionListMap.current
							.get(type.value)!
							.add(span.uuid.uuidString);
					}
				});
			});
			setTypeList([...primaryTypeRef.current]);

			setSpanToTypeMap(_initializeSpanToTypeMap());
		}
	}, [firstRun]);

	// Add the event listener to open the searchbar. Listens to alphanumeric key presses or command/control + f
	// to trigger
	useEffect(() => {
		const keydownHandler = (e: KeyboardEvent) => {
			if (
				!(e.metaKey || e.ctrlKey) &&
				((e.keyCode < 58 && e.keyCode > 47) ||
					(e.keyCode > 64 && e.keyCode < 91))
			) {
				setSearchOpen(true);
				setSearchText("");
			} else if (e.key === "f" && (e.metaKey || e.ctrlKey)) {
				e.preventDefault();
				setSearchOpen(true);
			}
		};
		if (!searchOpen) {
			document.addEventListener("keydown", keydownHandler);
		} else {
			document.removeEventListener("keydown", keydownHandler);
		}
		return () => document.removeEventListener("keydown", keydownHandler);
	}, [searchOpen]);

	// When searchText changes, filter the entityList
	useEffect(() => {
		const _activeSituationType =
			spanToTypeMap.get(props.activeSituation[0]?.uuidString || "") || "";
		const typeRef =
			props.annotationScheme.subkind === "entity" ||
			(props.annotationScheme.subkind === "situation" &&
				mentionModel === "situation")
				? primaryTypeRef.current
				: roleSetMap.current.get(_activeSituationType) ||
				  roleSetMap.current.get("types") ||
				  [];

		if (searchOpen && searchText) {
			const newList = typeRef.filter((type: Type) => {
				return matcher(searchText, type.value || "");
			});
			if (props.activeTypeIds.length) {
				const newIndex = newList.findIndex((type: Type) => {
					return (
						type.uuid.uuidString ===
						props.activeTypeIds[0].uuidString
					);
				});
				setFocusedType(newIndex === -1 ? undefined : newIndex);
			}
			setTypeList(newList);
		} else {
			setTypeList(typeRef);
		}
	}, [searchText, searchOpen]);

	// When activeTypeIds is set to an empty array, set focusedEntity to undefined
	useEffect(() => {
		if (!props.activeTypeIds.length) {
			setFocusedType(undefined);
		}
	}, [props.activeTypeIds]);

	// TODO: modifying props directly; bad on three levels
	useEffect(() => {
		if (!firstRun) {
			// props.entity.mentionIdList = props.entity.mentionIdList.filter((id) => props.spanMap.has(id.uuidString))
			typeList.forEach((type) => {
				typeToMentionListMap.current
					.get(type.value)
					?.forEach((mention) => {
						if (!props.spanMap.has(mention))
							typeToMentionListMap.current
								.get(type.value)
								?.delete(mention);
					});
			});
		}
	});

	// If a span is active and no entity is focused, focus the first entity
	useEffect(() => {
		if (props.activeStructureIds.length) {
			if (focusedType === undefined) {
				setFocusedType(0);
			}
		}
	}, [props.activeStructureIds]);

	useEffect(() => {
		const keyDownHandler = (e: KeyboardEvent) => {
			if (e.metaKey && e.key === "/") {
				if (
					props.annotationScheme.subkind === "situation" &&
					(mentionModel === "entity" ||
						(mentionModel === "situation" &&
							props.activeStructureIds.length &&
							spanToTypeMap.has(
								props.activeStructureIds[0][0].uuidString
							)))
				) {
					toggleFunction();
				}
			}
		};

		document.addEventListener("keydown", keyDownHandler);

		return () => document.removeEventListener("keydown", keyDownHandler);
	});

	const toggleFunction = () => {
		if (mentionModel === "situation") {
			const situationType =
				props.roleOntologyType === "general"
					? "types"
					: spanToTypeMap.get(
							props.activeStructureIds[0][0].uuidString
					  );
			const types = [...roleSetMap.current.get(situationType!)!];

			setFocusedType(undefined);
			setTypeList(types || []);
			props.activateSituation!([
				props.activeStructureIds[0][0],
				props.activeStructureIds[0][1],
			]);
			props.deactivateStructureIds!();
			displayToast("Switched to role-typing mode", "info");
		} else if (mentionModel === "entity") {
			setFocusedType(0);
			setTypeList(primaryTypeRef.current);
			props.deactivateSituation!();
			displayToast("Switched to situation-typing mode", "info");
		}
	};

	/**
	 * Helper function to change currently focused index (focusedEntity)
	 *
	 * @param index
	 * numerical value indicates index of entity in entityList to be focused, -1 indicates last entity
	 * 'up' refers to decrementing index by 1, 'down' referes to incrementing index by 1
	 */
	const selectTypeByIndex = (index?: number | "up" | "down"): void => {
		if (index === undefined) {
			setFocusedType(undefined);
		}
		if (index === -1) {
			setFocusedType(typeList.length - 1);
		} else if (
			typeof index === "number" &&
			index < typeList.length &&
			index >= 0
		) {
			setFocusedType(index);
		} else if (index === "up") {
			setFocusedType((i) => {
				if (i !== undefined && i === 0) {
					return typeList.length - 1;
				} else if (i !== undefined) {
					return i - 1;
				}
			});
		} else if (index === "down") {
			setFocusedType((i) => {
				if (i !== undefined && i === typeList.length - 1) {
					return 0;
				} else if (i !== undefined) {
					return i + 1;
				}
			});
		}
	};

	// Add support for clearing argList when SituationType changes
	const mapMentionToOntologyType = (mentionId: string, type: string) => {
		const map = new Map(spanToTypeMap);
		const prevType = map.get(mentionId);
		map.set(mentionId, type);

		if (prevType && prevType !== type) {
			typeToMentionListMap.current.get(prevType)?.delete(mentionId);

			if (props.annotationScheme.subkind === "situation") {
				const argumentMap = new Map(situationArgumentMap);
				const key =
					props.roleOntologyType === "particular"
						? prevType
						: "types";

				roleSetMap.current.get(key)?.forEach((t) => {
					argumentMap
						.get(props.activeStructureIds[0][0].uuidString)
						?.delete(t.value);
				});

				setSituationArgumentMap(argumentMap);
			}
		}
		if (!typeToMentionListMap.current.has(type))
			typeToMentionListMap.current.set(type, new Set<string>());

		if (
			type !== "" &&
			!typeToMentionListMap.current.get(type)?.has(mentionId)
		) {
			const toastMessage = `'${props.spanMap.get(
				mentionId
			)}' set to '${type}'`;
			displayToast(toastMessage, "success");
			addToUserInteractions(
				"set",
				{
					type:
						mentionModel === "entity"
							? "entityMention"
							: "situationMention",
					text: props.spanMap.get(mentionId) || "",
				},
				undefined,
				type
			);
		}
		typeToMentionListMap.current.get(type)?.add(mentionId);

		setSpanToTypeMap(map);
	};

	const deleteMentionFromOntologyType = (mentionId: string, type: string) => {
		typeToMentionListMap.current.get(type)?.delete(mentionId);
		const map = new Map(spanToTypeMap);
		map.delete(mentionId);

		const newSituationArgumentMap = new Map(situationArgumentMap);
		newSituationArgumentMap.delete(mentionId);

		const toastMessage = `'${props.spanMap.get(
			mentionId
		)}' removed from '${type}'`;

		setSpanToTypeMap(map);
		setSituationArgumentMap(newSituationArgumentMap);
		if (props.spanMap.get(mentionId)) {
			displayToast(toastMessage, "success");
			// addToUserInteractions(toastMessage);
			addToUserInteractions(
				"set",
				{
					type:
						mentionModel === "entity"
							? "entityMention"
							: "situationMention",
					text: props.spanMap.get(mentionId) || "",
				},
				undefined,
				""
			);
		}
	};

	const mapMentionToRoleType = (mentionId: string, type: string) => {
		const _activeSituation = props.activeSituation[0].uuidString;
		const map = new Map(situationArgumentMap);

		if (!map.has(_activeSituation)) {
			map.set(_activeSituation, new Map<string, Set<string>>());
		}

		const _activeSituationArgMap = map.get(_activeSituation);

		if (!_activeSituationArgMap!.has(type))
			_activeSituationArgMap!.set(type, new Set<string>());

		if (!_activeSituationArgMap!.get(type)?.has(mentionId)) {
			_activeSituationArgMap!.get(type)?.add(mentionId);
			setSituationArgumentMap(map);
			const toastMessage = `'${props.spanMap.get(
				mentionId
			)} added as '${type}' role to '${props.spanMap.get(
				_activeSituation
			)}'`;
			displayToast(toastMessage, "success");
			// addToUserInteractions(toastMessage);
			addToUserInteractions(
				"add",
				{
					type: "entityMention",
					text: props.spanMap.get(mentionId) || "",
				},
				undefined,
				undefined,
				type,
				undefined,
				props.spanMap.get(_activeSituation)
			);
		}
	};

	const deleteMentionFromRoleType = (mentionId: string, type: string) => {
		const _activeSituation = props.activeSituation[0].uuidString;
		const map = new Map(situationArgumentMap);
		const situationArgs = situationArgumentMap.get(_activeSituation);
		situationArgs?.get(type)?.delete(mentionId);

		const toastMessage = `'${props.spanMap.get(
			mentionId
		)} removed from '${type}' role to '${props.spanMap.get(
			_activeSituation
		)}'`;
		displayToast(toastMessage, "success");
		addToUserInteractions(
			"add",
			{
				type: "entityMention",
				text: props.spanMap.get(mentionId) || "",
			},
			undefined,
			undefined,
			type,
			props.spanMap.get(props.activeSituation[0].uuidString) + " mention"
		);
		setSituationArgumentMap(map);
	};

	if (props.activeSituation.length) {
		const _activeSituationType =
			spanToTypeMap.get(props.activeSituation[0].uuidString) || "";
		roleSetMap.current.get(_activeSituationType)?.forEach((t) => {
			disabledMap.set(t.value, false);
		});

		const rootNode =
			props.roleOntologyType === "particular"
				? situationExpressionTreeMap.current.get(_activeSituationType)
				: situationExpressionTreeMap.current.get("situation");

		if (rootNode)
			mentionTypeCheck(
				rootNode,
				situationArgumentMap.get(props.activeSituation[0].uuidString) ||
					new Map<string, Set<string>>(),
				disabledMap,
				false
			);
	}

	const mutateArgumentType = (roleUuid: string, argType: string) => {
		const _activeSituation = props.activeSituation[0].uuidString;
		const map = new Map(argumentTypeMap);

		if (argType === "") {
			map.get(_activeSituation)?.delete(roleUuid);
		} else {
			if (!map.has(_activeSituation)) {
				map.set(_activeSituation, new Map<string, string>());
			}

			map.get(_activeSituation)!.set(roleUuid, argType);
		}
	};

	const elements = typeList.map((t: Type, index: number) => {
		if (
			props.annotationScheme.subkind === "entity" ||
			mentionModel === "situation"
		) {
			return (
				<OntologyType
					key={t.uuid.uuidString}
					focusedType={focusedType}
					activeStructureIds={props.activeStructureIds}
					mapMentionToOntologyType={mapMentionToOntologyType}
					deleteMentionFromOntologyType={
						deleteMentionFromOntologyType
					}
					index={index}
					selectTypeByIndex={selectTypeByIndex}
					spanMap={props.spanMap}
					typeName={t.value}
					mentionList={
						typeToMentionListMap.current.get(t.value) ||
						new Set<string>()
					}
					disabled={false}
					currentMentionModel={mentionModel}
				/>
			);
		} else {
			const _activeSituation = props.activeSituation.length
				? props.activeSituation[0].uuidString
				: "";
			return (
				<OntologyType
					key={t.value}
					focusedType={focusedType}
					activeStructureIds={props.activeStructureIds}
					index={index}
					selectTypeByIndex={selectTypeByIndex}
					spanMap={props.spanMap}
					typeName={t.value}
					//Change MentionList for RoleSet
					mapMentionToOntologyType={mapMentionToRoleType}
					deleteMentionFromOntologyType={deleteMentionFromRoleType}
					mentionList={
						situationArgumentMap
							.get(_activeSituation)
							?.get(t.value) || new Set<string>()
					}
					disabled={disabledMap.get(t.value) || false}
					currentMentionModel={mentionModel}
				/>
			);
		}
	});

	const searchBar = searchOpen ? (
		<SearchBar
			searchText={searchText}
			setSearchText={setSearchText}
			setOpen={setSearchOpen}
			mentionModel={mentionModel}
			deactivateStrutureIds={props.deactivateStructureIds}
		/>
	) : null;

	const allowToggle =
		(mentionModel === "situation" &&
			props.activeStructureIds.length > 0 &&
			spanToTypeMap.has(props.activeStructureIds[0][0].uuidString)) ||
		mentionModel === "entity";

	const header =
		props.annotationScheme.subkind === "entity" ? (
			"Ontology"
		) : (
			<ToggleButton
				label={mentionModel === "situation" ? "Situations" : "Roles"}
				toggleFunction={toggleFunction}
				disabled={!allowToggle}
				displayToast={displayToast}
				checked={mentionModel === "entity"}
				message={
					props.activeStructureIds.length
						? "The selected span is not associated with a situation type"
						: "Please select a span that is associated with a situation type"
				}
			/>
		);

	return (
		<div className="menu">
			{searchBar}
			<div className={"header " + mentionModel}>
				<h1>{header}</h1>
			</div>
			<div className="content">{elements}</div>
			<div className="metadata-mention">
				<div className="content">
					{focusedType !== undefined ? (
						<div className="metadata-p-container">
							<p>
								{"Description: " +
									_getDescription(
										typeList[focusedType].description
									)}
							</p>
							<p>
								{_getExample(typeList[focusedType].examples)
									.length > 0
									? "Examples: " +
									  _getExample(
											typeList[focusedType].examples
									  )
									: null}
							</p>
						</div>
					) : (
						<p className="meta-empty-info">
							{"Select Type to show description and examples"}
						</p>
					)}
				</div>
			</div>
		</div>
	);
}

export default memo(MentionAnnotation);
